'use client';

import { CheckCircleIcon, ShieldCheckIcon, DocumentCheckIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface ValueChainPartnersSectionProps {
  currentLanguage: string;
}

const ValueChainPartnersSection = ({ currentLanguage }: ValueChainPartnersSectionProps) => {
  const content = {
    en: {
      heading: 'Empowering Value Chain Partners',
      subheading: 'Get Net Zero Ready with BRSR Lite & Net Zero Roadmap Accreditation',
      description: 'GenCarbon helps value chain partners become net zero ready and get accredited with BRSR Lite and Net Zero roadmap—essential requirements for empanelment with major corporations as part of the Net Zero 2030 and 2047 agenda.',
      benefits: [
        {
          icon: ShieldCheckIcon,
          title: 'BRSR Lite Accreditation',
          description: 'Get certified with Business Responsibility and Sustainability Reporting (BRSR) Lite framework, mandatory for value chain partners of listed companies.'
        },
        {
          icon: DocumentCheckIcon,
          title: 'Net Zero Roadmap Creation',
          description: 'Develop a comprehensive Net Zero roadmap aligned with India\'s 2030 and 2047 climate commitments, demonstrating your commitment to sustainability.'
        },
        {
          icon: CheckCircleIcon,
          title: 'Empanelment Readiness',
          description: 'Meet the essential criteria for getting empanelled with big companies for services and product delivery as sustainability becomes a key vendor selection factor.'
        },
        {
          icon: ChartBarIcon,
          title: 'Competitive Advantage',
          description: 'Stand out in vendor selection processes by showcasing verified sustainability credentials and commitment to climate action.'
        }
      ],
      whyEssential: 'Why This is Essential',
      essentialPoints: [
        'Major corporations require value chain partners to demonstrate net zero readiness',
        'BRSR Lite compliance is becoming mandatory for supplier empanelment',
        'Align with India\'s Net Zero 2030 and 2047 national agenda',
        'Access new business opportunities with sustainability-focused enterprises',
        'Future-proof your business against evolving ESG regulations'
      ],
      cta: 'Get Started with Accreditation',
      ctaSecondary: 'Learn More About BRSR Lite'
    },
    hi: {
      heading: 'मूल्य श्रृंखला भागीदारों को सशक्त बनाना',
      subheading: 'BRSR Lite और Net Zero रोडमैप मान्यता के साथ Net Zero तैयार हों',
      description: 'GenCarbon मूल्य श्रृंखला भागीदारों को नेट ज़ीरो तैयार बनने और BRSR Lite और Net Zero रोडमैप के साथ मान्यता प्राप्त करने में मदद करता है—Net Zero 2030 और 2047 एजेंडा के हिस्से के रूप में प्रमुख निगमों के साथ सूचीबद्ध होने के लिए आवश्यक आवश्यकताएं।',
      benefits: [
        {
          icon: ShieldCheckIcon,
          title: 'BRSR Lite मान्यता',
          description: 'सूचीबद्ध कंपनियों के मूल्य श्रृंखला भागीदारों के लिए अनिवार्य व्यावसायिक जिम्मेदारी और स्थिरता रिपोर्टिंग (BRSR) Lite ढांचे के साथ प्रमाणित हों।'
        },
        {
          icon: DocumentCheckIcon,
          title: 'Net Zero रोडमैप निर्माण',
          description: 'भारत की 2030 और 2047 जलवायु प्रतिबद्धताओं के अनुरूप एक व्यापक Net Zero रोडमैप विकसित करें, जो स्थिरता के प्रति आपकी प्रतिबद्धता को प्रदर्शित करता है।'
        },
        {
          icon: CheckCircleIcon,
          title: 'सूचीबद्धता तैयारी',
          description: 'सेवाओं और उत्पाद वितरण के लिए बड़ी कंपनियों के साथ सूचीबद्ध होने के लिए आवश्यक मानदंडों को पूरा करें क्योंकि स्थिरता एक प्रमुख विक्रेता चयन कारक बन जाती है।'
        },
        {
          icon: ChartBarIcon,
          title: 'प्रतिस्पर्धात्मक लाभ',
          description: 'सत्यापित स्थिरता प्रमाण-पत्र और जलवायु कार्रवाई के प्रति प्रतिबद्धता प्रदर्शित करके विक्रेता चयन प्रक्रियाओं में अलग दिखें।'
        }
      ],
      whyEssential: 'यह क्यों आवश्यक है',
      essentialPoints: [
        'प्रमुख निगमों को मूल्य श्रृंखला भागीदारों को नेट ज़ीरो तैयारी प्रदर्शित करने की आवश्यकता है',
        'BRSR Lite अनुपालन आपूर्तिकर्ता सूचीबद्धता के लिए अनिवार्य होता जा रहा है',
        'भारत के Net Zero 2030 और 2047 राष्ट्रीय एजेंडा के साथ संरेखित करें',
        'स्थिरता-केंद्रित उद्यमों के साथ नए व्यावसायिक अवसरों तक पहुंच',
        'विकसित हो रहे ESG नियमों के खिलाफ अपने व्यवसाय को भविष्य-प्रूफ करें'
      ],
      cta: 'मान्यता के साथ शुरू करें',
      ctaSecondary: 'BRSR Lite के बारे में और जानें'
    },
    te: {
      heading: 'విలువ గొలుసు భాగస్వాములను శక్తివంతం చేయడం',
      subheading: 'BRSR Lite & Net Zero రోడ్‌మ్యాప్ అక్రిడిటేషన్‌తో Net Zero సిద్ధంగా ఉండండి',
      description: 'GenCarbon విలువ గొలుసు భాగస్వాములు నెట్ జీరో సిద్ధంగా మారడానికి మరియు BRSR Lite మరియు Net Zero రోడ్‌మ్యాప్‌తో గుర్తింపు పొందడానికి సహాయపడుతుంది—Net Zero 2030 మరియు 2047 ఎజెండా భాగంగా ప్రధాన సంస్థలతో ఎంపానెల్‌మెంట్ కోసం అవసరమైన అవసరాలు.',
      benefits: [
        {
          icon: ShieldCheckIcon,
          title: 'BRSR Lite అక్రిడిటేషన్',
          description: 'జాబితా చేయబడిన కంపెనీల విలువ గొలుసు భాగస్వాములకు తప్పనిసరి వ్యాపార బాధ్యత మరియు స్థిరత్వ రిపోర్టింగ్ (BRSR) Lite ఫ్రేమ్‌వర్క్‌తో ధృవీకరించబడండి.'
        },
        {
          icon: DocumentCheckIcon,
          title: 'Net Zero రోడ్‌మ్యాప్ సృష్టి',
          description: 'భారతదేశం యొక్క 2030 మరియు 2047 వాతావరణ కట్టుబాట్లకు అనుగుణంగా సమగ్ర Net Zero రోడ్‌మ్యాప్‌ను అభివృద్ధి చేయండి, స్థిరత్వం ప్రति మీ నిబద్ధతను ప్రదర్శిస్తుంది.'
        },
        {
          icon: CheckCircleIcon,
          title: 'ఎంపానెల్‌మెంట్ సిద్ధత',
          description: 'స్థిరత్వం కీలక విక్రేత ఎంపిక కారకంగా మారుతున్నందున సేవలు మరియు ఉత్పత్తి పంపిణీ కోసం పెద్ద కంపెనీలతో ఎంపానెల్ కావడానికి అవసరమైన ప్రమాణాలను తీర్చండి.'
        },
        {
          icon: ChartBarIcon,
          title: 'పోటీ ప్రయోజనం',
          description: 'ధృవీకరించబడిన స్థిరత్వ ఆధారాలు మరియు వాతావరణ చర్యకు నిబద్ధతను ప్రదర్శించడం ద్వారా విక్రేత ఎంపిక ప్రక్రియలలో ప్రత్యేకంగా నిలబడండి.'
        }
      ],
      whyEssential: 'ఇది ఎందుకు అవసరం',
      essentialPoints: [
        'ప్రధాన సంస్థలు విలువ గొలుసు భాగస్వాములు నెట్ జీరో సిద్ధతను ప్రదర్శించాలని కోరుతున్నాయి',
        'BRSR Lite సమ్మతి సరఫరాదారు ఎంపానెల్‌మెంట్ కోసం తప్పనిసరి అవుతోంది',
        'భారతదేశం యొక్క Net Zero 2030 మరియు 2047 జాతీయ ఎజెండాతో సమలేఖనం చేయండి',
        'స్థిరత్వ-కేంద్రీకృత సంస్థలతో కొత్త వ్యాపార అవకాశాలను యాక్సెస్ చేయండి',
        'అభివృద్ధి చెందుతున్న ESG నిబంధనలకు వ్యతిరేకంగా మీ వ్యాపారాన్ని భవిష్యత్తు-ప్రూఫ్ చేయండి'
      ],
      cta: 'అక్రిడిటేషన్‌తో ప్రారంభించండి',
      ctaSecondary: 'BRSR Lite గురించి మరింత తెలుసుకోండి'
    },
    ar: {
      heading: 'تمكين شركاء سلسلة القيمة',
      subheading: 'كن جاهزًا للصفر الصافي مع اعتماد BRSR Lite وخارطة طريق الصفر الصافي',
      description: 'تساعد GenCarbon شركاء سلسلة القيمة على أن يصبحوا جاهزين للصفر الصافي والحصول على اعتماد BRSR Lite وخارطة طريق الصفر الصافي - المتطلبات الأساسية للإدراج مع الشركات الكبرى كجزء من أجندة الصفر الصافي 2030 و 2047.',
      benefits: [
        {
          icon: ShieldCheckIcon,
          title: 'اعتماد BRSR Lite',
          description: 'احصل على شهادة مع إطار المسؤولية التجارية وإعداد التقارير عن الاستدامة (BRSR) Lite، الإلزامي لشركاء سلسلة القيمة للشركات المدرجة.'
        },
        {
          icon: DocumentCheckIcon,
          title: 'إنشاء خارطة طريق الصفر الصافي',
          description: 'قم بتطوير خارطة طريق شاملة للصفر الصافي تتماشى مع التزامات الهند المناخية لعامي 2030 و 2047، مما يوضح التزامك بالاستدامة.'
        },
        {
          icon: CheckCircleIcon,
          title: 'جاهزية الإدراج',
          description: 'استوفِ المعايير الأساسية للإدراج مع الشركات الكبرى للخدمات وتسليم المنتجات حيث أصبحت الاستدامة عاملاً رئيسيًا في اختيار البائعين.'
        },
        {
          icon: ChartBarIcon,
          title: 'ميزة تنافسية',
          description: 'تميز في عمليات اختيار البائعين من خلال عرض بيانات الاعتماد المعتمدة للاستدامة والالتزام بالعمل المناخي.'
        }
      ],
      whyEssential: 'لماذا هذا ضروري',
      essentialPoints: [
        'تطلب الشركات الكبرى من شركاء سلسلة القيمة إظهار الجاهزية للصفر الصافي',
        'أصبح الامتثال لـ BRSR Lite إلزاميًا لإدراج الموردين',
        'التوافق مع أجندة الهند الوطنية للصفر الصافي 2030 و 2047',
        'الوصول إلى فرص عمل جديدة مع المؤسسات التي تركز على الاستدامة',
        'حماية عملك في المستقبل ضد لوائح ESG المتطورة'
      ],
      cta: 'ابدأ مع الاعتماد',
      ctaSecondary: 'تعرف على المزيد حول BRSR Lite'
    },
    bn: {
      heading: 'মূল্য শৃঙ্খল অংশীদারদের ক্ষমতায়ন',
      subheading: 'BRSR Lite এবং Net Zero রোডম্যাপ স্বীকৃতির সাথে Net Zero প্রস্তুত হন',
      description: 'GenCarbon মূল্য শৃঙ্খল অংশীদারদের নেট জিরো প্রস্তুত হতে এবং BRSR Lite এবং Net Zero রোডম্যাপের সাথে স্বীকৃত হতে সাহায্য করে—Net Zero 2030 এবং 2047 এজেন্ডার অংশ হিসাবে প্রধান কর্পোরেশনগুলির সাথে তালিকাভুক্তির জন্য প্রয়োজনীয় প্রয়োজনীয়তা।',
      benefits: [
        {
          icon: ShieldCheckIcon,
          title: 'BRSR Lite স্বীকৃতি',
          description: 'তালিকাভুক্ত কোম্পানিগুলির মূল্য শৃঙ্খল অংশীদারদের জন্য বাধ্যতামূলক ব্যবসায়িক দায়বদ্ধতা এবং স্থায়িত্ব রিপোর্টিং (BRSR) Lite কাঠামোর সাথে প্রত্যয়িত হন।'
        },
        {
          icon: DocumentCheckIcon,
          title: 'Net Zero রোডম্যাপ তৈরি',
          description: 'ভারতের 2030 এবং 2047 জলবায়ু প্রতিশ্রুতির সাথে সামঞ্জস্যপূর্ণ একটি ব্যাপক Net Zero রোডম্যাপ বিকাশ করুন, যা স্থায়িত্বের প্রতি আপনার প্রতিশ্রুতি প্রদর্শন করে।'
        },
        {
          icon: CheckCircleIcon,
          title: 'তালিকাভুক্তি প্রস্তুতি',
          description: 'সেবা এবং পণ্য সরবরাহের জন্য বড় কোম্পানিগুলির সাথে তালিকাভুক্ত হওয়ার জন্য প্রয়োজনীয় মানদণ্ড পূরণ করুন কারণ স্থায়িত্ব একটি মূল বিক্রেতা নির্বাচন ফ্যাক্টর হয়ে উঠছে।'
        },
        {
          icon: ChartBarIcon,
          title: 'প্রতিযোগিতামূলক সুবিধা',
          description: 'যাচাইকৃত স্থায়িত্ব শংসাপত্র এবং জলবায়ু কর্মের প্রতি প্রতিশ্রুতি প্রদর্শন করে বিক্রেতা নির্বাচন প্রক্রিয়াগুলিতে আলাদা হয়ে দাঁড়ান।'
        }
      ],
      whyEssential: 'এটি কেন প্রয়োজনীয়',
      essentialPoints: [
        'প্রধান কর্পোরেশনগুলি মূল্য শৃঙ্খল অংশীদারদের নেট জিরো প্রস্তুতি প্রদর্শন করতে চায়',
        'BRSR Lite সম্মতি সরবরাহকারী তালিকাভুক্তির জন্য বাধ্যতামূলক হয়ে উঠছে',
        'ভারতের Net Zero 2030 এবং 2047 জাতীয় এজেন্ডার সাথে সারিবদ্ধ করুন',
        'স্থায়িত্ব-কেন্দ্রিক উদ্যোগগুলির সাথে নতুন ব্যবসায়িক সুযোগ অ্যাক্সেস করুন',
        'বিকশিত ESG নিয়মের বিরুদ্ধে আপনার ব্যবসাকে ভবিষ্যত-প্রুফ করুন'
      ],
      cta: 'স্বীকৃতির সাথে শুরু করুন',
      ctaSecondary: 'BRSR Lite সম্পর্কে আরও জানুন'
    },
    as: {
      heading: 'মূল্য শৃংখল অংশীদাৰসকলক সৱলীকৰণ',
      subheading: 'BRSR Lite আৰু Net Zero ৰোডমেপ স্বীকৃতিৰ সৈতে Net Zero সাজু হওক',
      description: 'GenCarbon এ মূল্য শৃংখল অংশীদাৰসকলক নেট জিৰো সাজু হ\'বলৈ আৰু BRSR Lite আৰু Net Zero ৰোডমেপৰ সৈতে স্বীকৃত হ\'বলৈ সহায় কৰে—Net Zero 2030 আৰু 2047 এজেণ্ডাৰ অংশ হিচাপে প্ৰধান নিগমসমূহৰ সৈতে তালিকাভুক্তিৰ বাবে প্ৰয়োজনীয় প্ৰয়োজনীয়তা।',
      benefits: [
        {
          icon: ShieldCheckIcon,
          title: 'BRSR Lite স্বীকৃতি',
          description: 'তালিকাভুক্ত কোম্পানীসমূহৰ মূল্য শৃংখল অংশীদাৰসকলৰ বাবে বাধ্যতামূলক ব্যৱসায়িক দায়বদ্ধতা আৰু স্থায়িত্ব প্ৰতিবেদন (BRSR) Lite কাঠামোৰ সৈতে প্ৰমাণিত হওক।'
        },
        {
          icon: DocumentCheckIcon,
          title: 'Net Zero ৰোডমেপ সৃষ্টি',
          description: 'ভাৰতৰ 2030 আৰু 2047 জলবায়ু প্ৰতিশ্ৰুতিৰ সৈতে সামঞ্জস্যপূৰ্ণ এটা ব্যাপক Net Zero ৰোডমেপ বিকশিত কৰক, যি স্থায়িত্বৰ প্ৰতি আপোনাৰ প্ৰতিশ্ৰুতি প্ৰদৰ্শন কৰে।'
        },
        {
          icon: CheckCircleIcon,
          title: 'তালিকাভুক্তি সাজুতা',
          description: 'সেৱা আৰু পণ্য বিতৰণৰ বাবে ডাঙৰ কোম্পানীসমূহৰ সৈতে তালিকাভুক্ত হ\'বলৈ প্ৰয়োজনীয় মানদণ্ড পূৰণ কৰক কাৰণ স্থায়িত্ব এটা মূল বিক্ৰেতা নিৰ্বাচন কাৰক হৈ পৰিছে।'
        },
        {
          icon: ChartBarIcon,
          title: 'প্ৰতিযোগিতামূলক সুবিধা',
          description: 'সত্যাপিত স্থায়িত্ব প্ৰমাণপত্ৰ আৰু জলবায়ু কৰ্মৰ প্ৰতি প্ৰতিশ্ৰুতি প্ৰদৰ্শন কৰি বিক্ৰেতা নিৰ্বাচন প্ৰক্ৰিয়াসমূহত পৃথক হৈ থিয় দিয়ক।'
        }
      ],
      whyEssential: 'এইটো কিয় প্ৰয়োজনীয়',
      essentialPoints: [
        'প্ৰধান নিগমসমূহে মূল্য শৃংখল অংশীদাৰসকলক নেট জিৰো সাজুতা প্ৰদৰ্শন কৰিবলৈ বিচাৰে',
        'BRSR Lite সম্মতি যোগানকাৰী তালিকাভুক্তিৰ বাবে বাধ্যতামূলক হৈ পৰিছে',
        'ভাৰতৰ Net Zero 2030 আৰু 2047 ৰাষ্ট্ৰীয় এজেণ্ডাৰ সৈতে সংৰেখিত কৰক',
        'স্থায়িত্ব-কেন্দ্ৰিক উদ্যোগসমূহৰ সৈতে নতুন ব্যৱসায়িক সুযোগ প্ৰৱেশ কৰক',
        'বিকশিত ESG নিয়মৰ বিৰুদ্ধে আপোনাৰ ব্যৱসায়ক ভৱিষ্যত-প্ৰুফ কৰক'
      ],
      cta: 'স্বীকৃতিৰ সৈতে আৰম্ভ কৰক',
      ctaSecondary: 'BRSR Lite ৰ বিষয়ে অধিক জানক'
    }
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-green-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {lang.heading}
          </h2>
          <p className="text-xl md:text-2xl text-green-600 font-semibold mb-6">
            {lang.subheading}
          </p>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
            {lang.description}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {lang.benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-green-100"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Essential Section */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white mb-12">
          <h3 className="text-3xl font-bold mb-6 text-center">{lang.whyEssential}</h3>
          <ul className="space-y-4 max-w-3xl mx-auto">
            {lang.essentialPoints.map((point, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircleIcon className="w-6 h-6 flex-shrink-0 mt-1" />
                <span className="text-lg">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            href="/get-started"
            className="px-8 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300 shadow-lg hover:shadow-xl text-center min-w-[250px]"
          >
            {lang.cta}
          </Link>
          <Link
            href="/brsr-esg-compliance-hub"
            className="px-8 py-4 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-300 shadow-lg hover:shadow-xl border-2 border-green-600 text-center min-w-[250px]"
          >
            {lang.ctaSecondary}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ValueChainPartnersSection;